// EPGStationスタイルの動画プレイヤーコンポーネント
// ARIB STD-B24 風の字幕オーバーレイ・字幕切替トグル・主/副音声・全画面・PiP対応
//
// 映像は `<video>` ではなく `<canvas>` へ描く。地デジは MPEG-2 Video で、
// ブラウザ内蔵のデコーダが受け付けないため、WASM で復号して自前で描くしかない
// （docs/FINDINGS.md 7章）。音声も `<video>` 経由ではなく WebCodecs と
// AudioContext で鳴らすので、再生・音量・字幕はすべて外から差し込む。
// 見た目と文言はそのまま。

export interface VideoPlayerOptions {
  filePlayback?: boolean;
  videoSrc?: string | undefined;
  autoplay?: boolean | undefined;
  programTitle?: string | undefined;
  onSubtitleToggle?: ((enabled: boolean) => void) | undefined;
  onAudioTrackChange?: ((track: 'main' | 'sub') => void) | undefined;
  /** 再生・停止。live なので「一時停止」は受信の停止と再開になる。 */
  onPlayPause?: ((playing: boolean) => void) | undefined;
  /** 音量 (0-1) とミュート。Web Audio 側へ渡す。 */
  onVolumeChange?: ((volume: number, muted: boolean) => void) | undefined;
}

export class VideoPlayer {
  public readonly element: HTMLElement;
  /** 映像の描画先。制御を Worker へ渡すのは呼び出し側の仕事。 */
  public media: HTMLCanvasElement;
  /** 字幕を描く層。映像と同じ位置・大きさに保つ（setVideoRect）。 */
  private captionLayer: HTMLElement;
  private statusText: HTMLElement;
  private controlsBar: HTMLElement;
  private playBtn: HTMLButtonElement;
  private playIcon: HTMLElement;
  private volumeBtn: HTMLButtonElement;
  private volumeIcon: HTMLElement;
  private volumeSlider: HTMLInputElement;
  private subtitleBtn: HTMLButtonElement;
  private audioBtn: HTMLButtonElement;
  private pipBtn: HTMLButtonElement;
  private fullscreenBtn: HTMLButtonElement;

  private isSubtitlesEnabled = true;
  private currentAudioTrack: 'main' | 'sub' = 'main';
  private hideControlsTimer: number | null = null;
  private isPlaying = true;
  private pipVideo: HTMLVideoElement | null = null;
  private volume = 1;
  private muted = false;
  /** 差し替えた canvas にも掛け直すため、ハンドラを持っておく。 */
  private togglePlay: () => void = () => {};

  constructor(options: VideoPlayerOptions = {}) {
    this.element = document.createElement('div');
    this.element.className = 'video-player-container';
    this.element.tabIndex = 0;

    // 1. 映像の描画先
    this.media = this.createMedia();

    // 2. 字幕を描く層
    // 字幕は放送の指定どおりの位置に描く（caption-canvas.ts）。描く先は映像と
    // 同じ位置・大きさの層で、操作を妨げない。
    this.captionLayer = document.createElement('div');
    this.captionLayer.className = 'video-caption-layer';
    this.captionLayer.style.position = 'absolute';
    this.captionLayer.style.inset = '0';
    this.captionLayer.style.zIndex = '10';
    this.captionLayer.style.pointerEvents = 'none';
    this.captionLayer.style.overflow = 'hidden';

    // 2b. 受信の状況とエラーの表示先。
    // **字幕の枠には混ぜない。**放送から来た字幕とアプリのメッセージが
    // 見分けられなくなる。実際、選局の失敗が字幕として大きく出ていた。
    this.statusText = document.createElement('div');
    this.statusText.className = 'video-player-status';
    this.statusText.style.cssText = [
      'position:absolute',
      'left:50%',
      'top:50%',
      'transform:translate(-50%, -50%)',
      'max-width:80%',
      'padding:6px 12px',
      'border-radius:4px',
      'background-color:rgba(0, 0, 0, 0.6)',
      'color:#fff',
      'font-size:0.8125rem',
      'line-height:1.5',
      'text-align:center',
      'pointer-events:none',
      'z-index:11',
      'display:none',
    ].join(';');

    // 3. コントロールバー
    this.controlsBar = document.createElement('div');
    this.controlsBar.className = 'video-controls-bar';

    // 左側: 再生/停止, LIVEバッジ, 音量
    const leftGroup = document.createElement('div');
    leftGroup.className = 'video-controls-group left';

    this.playBtn = document.createElement('button');
    this.playBtn.type = 'button';
    this.playBtn.className = 'player-btn play-btn';
    this.playBtn.setAttribute('aria-label', '再生 / 一時停止');
    this.playIcon = document.createElement('span');
    this.updatePlayIcon(true);
    this.playBtn.append(this.playIcon);

    const liveBadge = document.createElement('div');
    liveBadge.className = 'player-live-badge';
    liveBadge.innerHTML = options.filePlayback ? '録画' : `<span class="live-dot"></span>LIVE`;

    this.volumeBtn = document.createElement('button');
    this.volumeBtn.type = 'button';
    this.volumeBtn.className = 'player-btn volume-btn';
    this.volumeBtn.setAttribute('aria-label', 'ミュート切り替え');
    this.volumeIcon = document.createElement('span');
    this.updateVolumeIcon(true);
    this.volumeBtn.append(this.volumeIcon);

    this.volumeSlider = document.createElement('input');
    this.volumeSlider.type = 'range';
    this.volumeSlider.className = 'player-volume-slider';
    this.volumeSlider.min = '0';
    this.volumeSlider.max = '1';
    this.volumeSlider.step = '0.05';
    // **実際の音量を入れる。**固定で '0' を入れていたため、音は出ているのに
    // つまみだけ左端に張り付いていた。
    this.volumeSlider.value = String(this.volume);

    leftGroup.append(this.playBtn, liveBadge, this.volumeBtn, this.volumeSlider);

    // 右側: 字幕切替, 音声切替, PiP, 全画面
    const rightGroup = document.createElement('div');
    rightGroup.className = 'video-controls-group right';

    // 字幕 (CC) 切り替えボタン
    this.subtitleBtn = document.createElement('button');
    this.subtitleBtn.type = 'button';
    this.subtitleBtn.className = 'player-btn subtitle-btn active';
    this.subtitleBtn.title = '字幕の表示/非表示を切り替え (C)';
    this.subtitleBtn.innerHTML = `
      <svg viewBox="0 0 24 24" style="width:20px;height:20px;fill:currentColor">
        <path d="M19 4H5c-1.11 0-2 .9-2 2v12c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-8 7H9.5v-.5h-2v3h2V13H11v1c0 .55-.45 1-1 1H7c-.55 0-1-.45-1-1v-4c0-.55.45-1 1-1h3c.55 0 1 .45 1 1v1zm7 0h-1.5v-.5h-2v3h2V13H18v1c0 .55-.45 1-1 1h-3c-.55 0-1-.45-1-1v-4c0-.55.45-1 1-1h3c.55 0 1 .45 1 1v1z"/>
      </svg>
      <span class="btn-text">字幕 ON</span>
    `;

    // 音声切替ボタン
    this.audioBtn = document.createElement('button');
    this.audioBtn.type = 'button';
    this.audioBtn.className = 'player-btn audio-btn';
    this.audioBtn.title = '音声トラック切り替え (主/副)';
    this.audioBtn.innerHTML = `
      <svg viewBox="0 0 24 24" style="width:18px;height:18px;fill:currentColor">
        <path d="M12 3v9.28c-.47-.17-.97-.28-1.5-.28C8.01 12 6 14.01 6 16.5S8.01 21 10.5 21c2.31 0 4.2-1.75 4.45-4H15V6h4V3h-7z"/>
      </svg>
      <span class="btn-text">主音声</span>
    `;

    // PiP ボタン
    this.pipBtn = document.createElement('button');
    this.pipBtn.type = 'button';
    this.pipBtn.className = 'player-btn pip-btn';
    this.pipBtn.title = 'ピクチャー・イン・ピクチャー';
    this.pipBtn.innerHTML = `
      <svg viewBox="0 0 24 24" style="width:20px;height:20px;fill:currentColor">
        <path d="M19 7h-8v6h8V7zm2-4H3c-1.1 0-2 .9-2 2v14c0 1.1.9 1.98 2 1.98h18c1.1 0 2-.88 2-1.98V5c0-1.1-.9-2-2-2zm0 16.01H3V4.98h18v14.03z"/>
      </svg>
    `;

    // 全画面ボタン
    this.fullscreenBtn = document.createElement('button');
    this.fullscreenBtn.type = 'button';
    this.fullscreenBtn.className = 'player-btn fullscreen-btn';
    this.fullscreenBtn.title = '全画面表示 (F)';
    this.fullscreenBtn.innerHTML = `
      <svg viewBox="0 0 24 24" style="width:20px;height:20px;fill:currentColor">
        <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
      </svg>
    `;

    rightGroup.append(this.subtitleBtn, this.audioBtn, this.pipBtn, this.fullscreenBtn);
    this.controlsBar.append(leftGroup, rightGroup);

    // プレイヤーコンテナに組み立て
    this.element.append(this.media, this.captionLayer, this.statusText,
      this.controlsBar);

    // イベントバインド
    this.bindEvents(options);

    this.updatePlayIcon(true);
    this.updateVolumeIcon(false);
  }

  private bindEvents(options: VideoPlayerOptions): void {
    // 再生/一時停止クリック
    const togglePlay = () => {
      this.setPlaying(!this.isPlaying);
      options.onPlayPause?.(this.isPlaying);
    };

    this.togglePlay = togglePlay;
    this.playBtn.addEventListener('click', togglePlay);

    // 音量 & ミュート
    this.volumeBtn.addEventListener('click', () => {
      if (this.muted || this.volume === 0) {
        this.muted = false;
        this.volume = 0.5;
        this.volumeSlider.value = '0.5';
        this.updateVolumeIcon(false);
      } else {
        this.muted = true;
        this.volumeSlider.value = '0';
        this.updateVolumeIcon(true);
      }
      options.onVolumeChange?.(this.volume, this.muted);
    });

    this.volumeSlider.addEventListener('input', () => {
      const val = Number(this.volumeSlider.value);
      this.volume = val;
      this.muted = val === 0;
      this.updateVolumeIcon(val === 0);
      options.onVolumeChange?.(this.volume, this.muted);
    });

    // 字幕切替クリック
    this.subtitleBtn.addEventListener('click', () => {
      this.setSubtitlesEnabled(!this.isSubtitlesEnabled);
      options.onSubtitleToggle?.(this.isSubtitlesEnabled);
    });

    // 音声切替クリック
    this.audioBtn.addEventListener('click', () => {
      this.currentAudioTrack = this.currentAudioTrack === 'main' ? 'sub' : 'main';
      const textSpan = this.audioBtn.querySelector('.btn-text');
      if (textSpan) {
        textSpan.textContent = this.currentAudioTrack === 'main' ? '主音声' : '副音声';
      }
      this.audioBtn.classList.toggle('sub', this.currentAudioTrack === 'sub');
      options.onAudioTrackChange?.(this.currentAudioTrack);
    });

    // PiP クリック
    // Picture-in-Picture は `<video>` にしか掛けられないので、canvas の
    // captureStream を流す video を隠して持ち、それを渡す。
    this.pipBtn.addEventListener('click', async () => {
      try {
        if (document.pictureInPictureElement) {
          await document.exitPictureInPicture();
          return;
        }
        if (!document.pictureInPictureEnabled) return;
        if (this.pipVideo === null) {
          const video = document.createElement('video');
          video.muted = true;
          video.playsInline = true;
          video.style.display = 'none';
          video.srcObject = this.media.captureStream();
          this.element.append(video);
          this.pipVideo = video;
        }
        await this.pipVideo.play();
        await this.pipVideo.requestPictureInPicture();
      } catch (err) {
        console.warn('PiP error:', err);
      }
    });

    // 全画面クリック
    this.fullscreenBtn.addEventListener('click', async () => {
      try {
        if (!document.fullscreenElement) {
          await this.element.requestFullscreen();
        } else {
          await document.exitFullscreen();
        }
      } catch (err) {
        console.warn('Fullscreen error:', err);
      }
    });

    // コントロールバーの自動非表示 (マウス停止時)
    const showControls = () => {
      this.controlsBar.classList.remove('hidden');
      if (this.hideControlsTimer !== null) clearTimeout(this.hideControlsTimer);
      if (this.isPlaying) {
        this.hideControlsTimer = window.setTimeout(() => {
          this.controlsBar.classList.add('hidden');
        }, 3000);
      }
    };

    this.element.addEventListener('mousemove', showControls);
    this.element.addEventListener('touchstart', showControls, { passive: true });
    this.element.addEventListener('mouseleave', () => {
      if (this.isPlaying) {
        this.controlsBar.classList.add('hidden');
      }
    });

    // キーボードショートカット
    this.element.addEventListener('keydown', (e) => {
      if (e.key === ' ' || e.key === 'k') {
        e.preventDefault();
        togglePlay();
      } else if (e.key === 'm') {
        e.preventDefault();
        this.volumeBtn.click();
      } else if (e.key === 'c') {
        e.preventDefault();
        this.subtitleBtn.click();
      } else if (e.key === 'f') {
        e.preventDefault();
        this.fullscreenBtn.click();
      }
    });
  }

  private createMedia(): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.className = 'video-player-media';
    // 実際の寸法は sequence が分かった時点で描画側が設定する。
    canvas.width = 1280;
    canvas.height = 720;
    canvas.addEventListener('click', () => { this.togglePlay(); });
    return canvas;
  }

  /**
   * 描画先を作り直し、Worker へ渡す OffscreenCanvas を返す。
   *
   * `transferControlToOffscreen()` は**同じ canvas に二度掛けられない**。
   * 一時停止から再生へ戻すときに同じ要素へ掛け直すと例外になり、再生へ
   * 復帰できなかった。要素そのものを取り替える。
   */
  public takeOffscreen(): OffscreenCanvas {
    const next = this.createMedia();
    this.media.replaceWith(next);
    this.media = next;
    // PiP 中なら、流しているストリームも新しい canvas のものへ繋ぎ直す。
    if (this.pipVideo !== null) this.pipVideo.srcObject = next.captureStream();
    return next.transferControlToOffscreen();
  }

  /** いまの音量とミュート。受信を開き直したときに鳴らし直す側へ渡す。 */
  public get audioState(): { volume: number; muted: boolean } {
    return { volume: this.volume, muted: this.muted };
  }

  public setPlaying(playing: boolean): void {
    this.isPlaying = playing;
    this.updatePlayIcon(playing);
    if (!playing) {
      if (this.hideControlsTimer !== null) clearTimeout(this.hideControlsTimer);
      this.hideControlsTimer = null;
      this.controlsBar.classList.remove('hidden');
    }
  }

  /** 受信の状況とエラーを出す。空文字で消える。 */
  public setStatusText(text: string): void {
    this.statusText.textContent = text;
    this.statusText.style.display = text === '' ? 'none' : '';
  }

  /** 字幕を描く先。LiveSession の captionHost に渡す。 */
  public get captionHost(): HTMLElement {
    return this.captionLayer;
  }

  public setSubtitlesEnabled(enabled: boolean): void {
    this.isSubtitlesEnabled = enabled;
    this.captionLayer.style.visibility = enabled ? '' : 'hidden';
    this.subtitleBtn.classList.toggle('active', enabled);

    const textSpan = this.subtitleBtn.querySelector('.btn-text');
    if (textSpan) {
      textSpan.textContent = enabled ? '字幕 ON' : '字幕 OFF';
    }
  }

  /**
   * 映像を箱の中の一部へ寄せる。データ放送が映像の位置と大きさを決めるとき
   * に使う。値は箱に対する割合（0〜1）。null で全面へ戻す。
   */
  public setVideoRect(rect: { left: number; top: number; width: number; height: number } | null): void {
    const style = this.media.style;
    // 字幕の層も映像に付いて行く。字幕の座標は映像の上のもの。
    const caption = this.captionLayer.style;
    if (rect === null) {
      style.removeProperty('position');
      style.removeProperty('left');
      style.removeProperty('top');
      style.removeProperty('width');
      style.removeProperty('height');
      caption.inset = '0';
      caption.removeProperty('width');
      caption.removeProperty('height');
      return;
    }
    style.position = 'absolute';
    style.left = `${rect.left * 100}%`;
    style.top = `${rect.top * 100}%`;
    style.width = `${rect.width * 100}%`;
    style.height = `${rect.height * 100}%`;
    caption.inset = 'auto';
    caption.left = style.left;
    caption.top = style.top;
    caption.width = style.width;
    caption.height = style.height;
  }

  /** 映像の実寸が分かった時点で表示比を合わせる。 */
  public setAspectRatio(ratio: string): void {
    this.element.style.aspectRatio = ratio;
  }

  private updatePlayIcon(isPlaying: boolean): void {
    this.playIcon.innerHTML = isPlaying
      ? `<svg viewBox="0 0 24 24" style="width:22px;height:22px;fill:currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>`
      : `<svg viewBox="0 0 24 24" style="width:22px;height:22px;fill:currentColor"><path d="M8 5v14l11-7z"/></svg>`;
  }

  private updateVolumeIcon(isMuted: boolean): void {
    this.volumeIcon.innerHTML = isMuted
      ? `<svg viewBox="0 0 24 24" style="width:20px;height:20px;fill:currentColor"><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/></svg>`
      : `<svg viewBox="0 0 24 24" style="width:20px;height:20px;fill:currentColor"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/></svg>`;
  }

  public destroy(): void {
    if (this.hideControlsTimer !== null) {
      clearTimeout(this.hideControlsTimer);
      this.hideControlsTimer = null;
    }
    if (this.pipVideo !== null) {
      this.pipVideo.srcObject = null;
      this.pipVideo.remove();
      this.pipVideo = null;
    }
  }
}
